import { useEffect, useState } from "react";
import { Calendar, X } from "lucide-react";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/contexts/LanguageContext";
import { trackBlogEngagement, trackBookCallClick } from "@/lib/analytics";

const CALENDLY_URL = "https://calendly.com/dariusz-potepa-agapeco/discoverycall";

interface StickyArticleCTAProps {
  postId?: string;
  postSlug?: string;
  postTitle?: string;
}

/**
 * Bottom sticky CTA bar shown on blog posts after the user has scrolled
 * past ~25% of the article. Single action: "book a discovery call".
 * Dismissible per session. Interactions tracked via `trackBlogEngagement`.
 */
const StickyArticleCTA = ({ postId, postSlug, postTitle }: StickyArticleCTAProps) => {
  const t = useTranslations();
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem("blog-sticky-cta-dismissed") === "1") {
      setDismissed(true);
      return;
    }

    const onScroll = () => {
      const article = document.querySelector("article");
      const scrollTop = window.scrollY;
      let pct = 0;
      if (article instanceof HTMLElement) {
        const start = article.offsetTop - 80;
        const total = article.offsetHeight - window.innerHeight + 80;
        pct = total > 0 ? ((scrollTop - start) / total) * 100 : 0;
      } else {
        const docH = document.documentElement.scrollHeight - window.innerHeight;
        pct = docH > 0 ? (scrollTop / docH) * 100 : 0;
      }
      setVisible(pct >= 25);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem("blog-sticky-cta-dismissed", "1");
    } catch {
      // ignore
    }
    if (postId) {
      trackBlogEngagement("cta_dismiss", {
        postId,
        slug: postSlug,
        title: postTitle,
        language,
        extra: { source: "sticky_bar" },
      });
    }
  };

  const handleBookCall = () => {
    trackBookCallClick("blog_sticky_bar", { language, calendlyUrl: CALENDLY_URL, extra: { post_id: postId, post_slug: postSlug } });
    if (postId) {
      trackBlogEngagement("cta_book_call_click", {
        postId,
        slug: postSlug,
        title: postTitle,
        language,
        extra: { source: "sticky_bar", outbound_url: CALENDLY_URL },
      });
    }
  };

  if (dismissed || !visible) return null;

  const sticky = t.blog.stickyCta;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border/50 bg-background/95 backdrop-blur-xl shadow-[0_-8px_32px_-12px_hsl(var(--primary)/0.35)] animate-in slide-in-from-bottom-4 duration-300"
      role="region"
      aria-label={sticky.ariaLabel}
    >
      <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-3 sm:gap-6">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="hidden sm:flex w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent items-center justify-center shrink-0">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm sm:text-base leading-tight">{sticky.title}</p>
              <p className="text-xs text-foreground/65 leading-tight hidden sm:block">
                {sticky.description}
              </p>
            </div>
          </div>

          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleBookCall}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold hover:opacity-90 transition-opacity whitespace-nowrap shrink-0"
          >
            <Calendar className="w-4 h-4" />
            <span className="hidden sm:inline">{sticky.bookCall}</span>
            <span className="sm:hidden">{sticky.bookCallShort}</span>
          </a>

          <button
            type="button"
            onClick={handleDismiss}
            aria-label={sticky.dismiss}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default StickyArticleCTA;
